import React, { FC, useState, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from 'components/Card'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Table, TableHead, TableRow, TableHeadCell, TableBody, TableCell } from 'components/Table'
import { SelectInput } from 'components/SelectInput'
import { Button } from 'components/Button'
import { Access, ProjectPermissions } from 'models/user-permissions'
import { Alert } from 'components/Alert'
import { useUpdatePermissions } from 'services/user'

type ProjectPermissionsProps = {
  userId: string
  permissions: ProjectPermissions[]
}

export const ProjectPermissionsTable: FC<ProjectPermissionsProps> = ({ userId, permissions }) => {
  const { t } = useTranslation()
  const [projects, setProjects] = useState(permissions)
  const [updatePermissions] = useUpdatePermissions(userId)
  const [status, setStatus] = useState('idle')

  const handleProjectChange = (access: string, id: number) => {
    const newData = projects.map((project) =>
      project.id === id ? { ...project, access } : project,
    )
    setProjects(newData)
  }

  const handleProjectsSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus('loading')

    updatePermissions(
      { projects },
      {
        onError: () => setStatus('error'),
        onSuccess: () => setStatus('success'),
        onSettled: () => {
          setTimeout(() => setStatus('idle'), 1000)
        },
      },
    )
  }

  return (
    <Card>
      <Stack space="large">
        <Stack space="xsmall">
          <Heading size="secondary">{t('user.edit.card.project.title')}</Heading>
          <Text>{t('user.edit.card.project.desc')}</Text>
        </Stack>

        <form onSubmit={handleProjectsSubmit}>
          <Stack space="medium">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell>
                    <Text weight="strong">{t('user.edit.card.project.form.name')}</Text>
                  </TableHeadCell>
                  <TableHeadCell>
                    <Text weight="strong">{t('user.edit.card.project.form.permissions')}</Text>
                  </TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={`project-${project.id}`}>
                    <TableCell>
                      <Text weight="strong">{project.name}</Text>
                    </TableCell>
                    <TableCell>
                      <SelectInput
                        borderless
                        options={[
                          {
                            value: Access.NO_ACCESS,
                            label: t(`user_permissions.${Access.NO_ACCESS}`),
                          },
                          {
                            value: Access.READ,
                            label: t(`user_permissions.${Access.READ}`),
                          },
                          {
                            value: Access.WRITE,
                            label: t(`user_permissions.${Access.WRITE}`),
                          },
                        ]}
                        value={{
                          value: project.access,
                          label: t(`user_permissions.${project.access}`),
                        }}
                        label={t('user.edit.card.project.form.permissions')}
                        onChange={(option) =>
                          option && handleProjectChange(option.value.toString(), project.id)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button type="submit" loading={status === 'loading'}>
              {t('user.edit.save')}
            </Button>
          </Stack>
        </form>

        {status === 'success' && (
          <Alert tone="positive" full>
            {t('common.messages.success')}
          </Alert>
        )}

        {status === 'error' && (
          <Alert tone="critical" full>
            {t('common.messages.error')}
          </Alert>
        )}
      </Stack>
    </Card>
  )
}

ProjectPermissionsTable.displayName = 'ProjectPermissionsTable'
